import { useCallback, useEffect, useRef, useState } from "react";
import type { Testimonial, Theme } from "./LeistungsseiteTemplate";

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
  theme?: Theme;
  autoAdvance?: boolean;
  autoAdvanceInterval?: number;
}

export default function TestimonialCarousel({
  testimonials,
  theme = "default",
  autoAdvance = true,
  autoAdvanceInterval = 6000,
}: TestimonialCarouselProps) {
  const [active, setActive] = useState(0);
  const [prefersReduced, setPrefersReduced] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const next = useCallback(() => {
    setActive((i) => (i + 1) % testimonials.length);
  }, [testimonials.length]);

  const prev = useCallback(() => {
    setActive((i) => (i - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    if (!autoAdvance || testimonials.length <= 1 || prefersReduced || isPaused) return;
    const id = setInterval(next, autoAdvanceInterval);
    return () => clearInterval(id);
  }, [autoAdvance, autoAdvanceInterval, prefersReduced, isPaused, next, testimonials.length]);

  if (testimonials.length === 0) return null;

  const isFuturistic = theme === "futuristic";
  const headingClass = isFuturistic ? "text-white" : "text-[#0A0A0A]";
  const bodyClass = isFuturistic ? "text-white/70" : "text-foreground-600";
  const mutedClass = isFuturistic ? "text-white/40" : "text-foreground-500";
  const borderClass = isFuturistic ? "border-[#4CAF50]/50" : "border-foreground-200";
  const hoverClass = isFuturistic ? "hover:bg-[#4CAF50]/10" : "hover:bg-foreground-100";
  const headingGlow = isFuturistic
    ? { textShadow: "0 0 28px rgba(76,175,80,0.45)" }
    : {};

  return (
    <figure
      className="mt-10"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div
        ref={containerRef}
        className="relative overflow-hidden"
        role="region"
        aria-roledescription="Karussell"
        aria-label="Kundenstimmen"
      >
        <div
          className={`flex ${prefersReduced ? "" : "transition-transform duration-500 ease-out"}`}
          style={{ transform: `translateX(-${active * 100}%)` }}
        >
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="w-full shrink-0"
              role="group"
              aria-roledescription="Folie"
              aria-label={`Zitat ${i + 1} von ${testimonials.length}`}
              aria-hidden={i !== active}
            >
              <blockquote
                className={`font-bold uppercase ${headingClass} leading-[1.05] break-words`}
                style={{
                  fontSize: "clamp(1.5rem, 5vw, 3rem)",
                  letterSpacing: "clamp(-0.4px, -0.12vw, -1.8px)",
                  ...headingGlow,
                }}
              >
                „{t.quote}"
              </blockquote>
              <figcaption className={`mt-8 ${bodyClass} text-sm md:text-base`}>
                <strong className={headingClass}>{t.name}</strong> · {t.location}
              </figcaption>
            </div>
          ))}
        </div>
      </div>

      {testimonials.length > 1 && (
        <div className="flex items-center gap-4 mt-8">
          <button
            type="button"
            onClick={prev}
            aria-label="Vorheriges Zitat"
            className={`w-10 h-10 rounded-full border ${borderClass} ${headingClass} ${hoverClass} flex items-center justify-center transition-colors`}
          >
            ←
          </button>
          <div className="flex items-center gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Zitat ${i + 1} anzeigen`}
                aria-current={i === active ? "true" : undefined}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === active ? "bg-[#4CAF50]" : `${mutedClass} bg-current`
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={next}
            aria-label="Nächstes Zitat"
            className={`w-10 h-10 rounded-full border ${borderClass} ${headingClass} ${hoverClass} flex items-center justify-center transition-colors`}
          >
            →
          </button>
        </div>
      )}
    </figure>
  );
}
